/**
 * Guiones del tutorial guiado.
 *
 * Un guion es una lista de pasos y cada paso dice: qué se cuenta, a qué
 * elemento apuntar, dónde hay que estar y cómo saber que el usuario ya lo ha
 * hecho. El motor (`GuidedTour.jsx`) se encarga del resto: llevar al usuario a
 * la pantalla, resaltar el elemento, comprobar el paso y avanzar solo cuando la
 * acción está hecha de verdad.
 *
 * Campos de un paso:
 *   id         identificador estable (se guarda el progreso por id)
 *   title      titular de la burbuja
 *   body       explicación (acepta **negrita** y listas con "· ")
 *   tip        consejo extra que solo sale en modo profesor
 *   route      a dónde hay que llevar al usuario antes de enseñar el paso
 *   target     selector CSS del elemento a resaltar (null = burbuja centrada)
 *   placement  top | bottom | left | right
 *   detect     (ctx, step) => boolean — true cuando el paso está completado
 *   advance    'auto' (avanza al detectar) | 'manual' (lo decide el usuario)
 *   writes     true si el paso escribe en la base de datos
 *
 * `ctx` es el vigilante del DOM (`tutorial/dom.js`): permite leer el estado de
 * la pantalla sin acoplarse a React.
 */

const NOMBRE_VPN = 'VPN corporativa [tutorial]';
const NOMBRE_RDS = 'Granja RDS [tutorial]';
const SERVICIO_RADIUS = 'RADIUS Corporativo';

export const TUTORIALS = [
  {
    id: 'vpn',
    icon: '🔐',
    title: 'Montar un servicio de VPN',
    subtitle: 'VPN corporativa → RADIUS → AD',
    summary:
      'Creas el servicio de VPN, le dices en qué equipo corre, le añades el acceso de cliente y lo enlazas con el RADIUS y el AD. Al terminar verás el enlace en las dos fichas.',
    minutes: 12,
    level: 'Intermedio',
    steps: [
      {
        id: 'intro',
        title: 'Vamos a montar una VPN de verdad',
        body:
          'En este tutorial vas a modelar la VPN corporativa tal como funciona en la realidad:\n' +
          '· el servicio de **VPN** cuelga de un cortafuegos,\n' +
          '· valida a los usuarios contra el **RADIUS**,\n' +
          '· y el RADIUS a su vez contra el **Active Directory**.\n\n' +
          'Yo te voy diciendo qué tocar y detecto cuándo lo haces para pasar al siguiente paso.',
        tip: 'Puedes parar cuando quieras: el progreso se guarda y al volver te dejo donde lo dejaste.',
        route: '/services',
        target: null,
        advance: 'manual',
      },
      {
        id: 'catalogo',
        title: 'Este es el catálogo de servicios',
        body:
          'Aquí viven los servicios de la CMDB. Cada fila es un servicio con su tipo, su criticidad y su alojamiento.\n\n' +
          'Todavía no hay una VPN de tutorial: vamos a crearla.',
        route: '/services',
        target: '.wol-container',
        placement: 'bottom',
        advance: 'manual',
      },
      {
        id: 'nuevo',
        title: 'Pulsa «+ Nuevo servicio»',
        body: 'Ese botón abre la ficha del servicio en blanco. Haz clic y sigue.',
        tip: 'El botón está en la cabecera del listado; si ya tienes servicios, aparece arriba a la derecha.',
        target: '.wol-header .wol-btn-primary',
        placement: 'bottom',
        auto: [
          { click: '.wol-header .wol-btn-primary' },
          { waitFor: '.dv-overlay' },
        ],
        detect: (ctx) => ctx.has('.dv-overlay'),
        advance: 'auto',
      },
      {
        id: 'titulo',
        title: 'Ponle nombre visible',
        body: `Escribe **${NOMBRE_VPN}** en el título grande. Es el nombre que verán los compañeros.`,
        tip: 'El título se guarda como «nombre visible»; el nombre interno es otro campo que rellenarás enseguida.',
        target: 'input.dv-record-title',
        placement: 'bottom',
        auto: [{ fill: ['input.dv-record-title', NOMBRE_VPN] }],
        detect: (ctx) => ctx.value('input.dv-record-title').length > 3,
        advance: 'auto',
      },
      {
        id: 'tipo',
        title: 'Dile que es una VPN',
        body:
          'En **Identificación → Tipo** elige **VPN**. El tipo decide qué pestañas extra tendrá la ficha ' +
          '(las VPN tienen su propio editor de topología).',
        target: 'select[name="service_type"]',
        placement: 'left',
        auto: [{ select: ['select[name="service_type"]', 'vpn'] }],
        detect: (ctx) => ctx.value('select[name="service_type"]') === 'vpn',
        advance: 'auto',
      },
      {
        id: 'nombre-interno',
        title: 'Nombre interno',
        body:
          'En la misma pestaña, escribe `vpn-tutorial` en **Nombre interno**. Este es el que se usa para buscar ' +
          'y para la API; el visible es el del título.',
        tip: 'Sin nombre interno no deja guardar: es el único campo obligatorio junto con el tipo.',
        target: 'input[name="name"]',
        placement: 'bottom',
        auto: [{ fill: ['input[name="name"]', 'vpn-tutorial'] }],
        detect: (ctx) => ctx.value('input[name="name"]').length > 2,
        advance: 'auto',
      },
      {
        id: 'guardar',
        title: 'Guarda el servicio',
        body:
          'Pulsa **Guardar** (arriba a la derecha). A partir de aquí el servicio existe en la base de datos ' +
          'y se le puede asociar equipo, accesos y relaciones.',
        tip: 'El botón está deshabilitado hasta que el formulario cambia: si no puedes pulsarlo, revisa que hayas tocado algo.',
        target: '.dv-topbar-actions .dv-btn-primary',
        placement: 'left',
        writes: true,
        trackServiceName: NOMBRE_VPN,
        auto: [
          { save: true, trackName: NOMBRE_VPN },
          { wait: 1200 },
        ],
        detect: (ctx) => ctx.hasServiceNamed(NOMBRE_VPN),
        advance: 'auto',
      },
      {
        id: 'alojamiento',
        title: '¿Dónde corre?',
        body:
          'Pestaña **Alojamiento**: la primera máquina de la lista es el equipo donde vive el servicio, y debajo ' +
          'están los CIs (equipos) que lo soportan.\n\n' +
          'Abre la pestaña y mira lo que hay.',
        target: '.dv-tabs-nav .dv-tab-btn:nth-child(3)',
        placement: 'bottom',
        auto: [{ tab: 'Alojamiento' }],
        advance: 'manual',
      },
      {
        id: 'host',
        title: 'Añade el equipo que lo sostiene',
        body:
          'Con la pestaña **Alojamiento** abierta, en «Equipos (CIs)», elige el tipo de equipo, búscalo y pulsa ' +
          '**Añadir CI**. Así la CMDB sabe de qué depende el servicio a nivel físico.',
        tip: 'El rol del CI es libre: «Servidor principal», «Balanceador», «VPN gw»...',
        autoNote: 'Este paso no lo hago solo: qué equipo sostiene la VPN depende de tu inventario, y elegirlo es justo lo que hay que aprender.',
        target: null,
        placement: 'bottom',
        writes: true,
        detect: (ctx) => ctx.hostRows() > 0,
        advance: 'auto',
      },
      {
        id: 'acceso',
        title: 'El acceso del cliente',
        body:
          'Pestaña **🔗 Accesos**: aquí se declara cómo entran los usuarios (VPN, RDP, HTTPS, SSH...).\n\n' +
          'Pulsa **Añadir acceso**, ponle nombre y elige el tipo **VPN**.',
        tip: 'El contador del botón smartbar «Accesos» sube solo cuando el acceso queda guardado.',
        autoNote: 'En la demo lo doy de alta por la API para no pelearme con el formulario; en la pantalla se hace con «Añadir acceso».',
        target: null,
        placement: 'bottom',
        writes: true,
        auto: [
          { tab: 'Accesos' },
          { addAccess: { name: 'Cliente VPN', access_type: 'vpn', protocol: 'udp', port: 1194, notes: 'Creado por el tutorial guiado' } },
        ],
        detect: (ctx) => ctx.smartCount('Accesos') > 0,
        advance: 'auto',
      },
      {
        id: 'disenador',
        title: 'Al diseñador de servicios',
        body:
          'Vamos al **Diseñador**: es el lienzo donde se dibuja el mapa del servicio. Te llevo con la VPN ya puesta ' +
          'en el centro.',
        tip: 'El Diseñador también se abre desde el menú: Servicios → Diseñador de servicios.',
        route: (state) => (state.serviceId ? `/services/designer?service=${state.serviceId}` : '/services/designer'),
        target: '.sd-canvas',
        placement: 'top',
        auto: [{ wait: 900 }, { waitFor: '.sd-canvas', optional: true }, { wait: 700 }],
        advance: 'manual',
      },
      {
        id: 'traer-radius',
        title: 'Trae el RADIUS al mapa',
        body:
          'En el catálogo de la izquierda busca **RADIUS Corporativo** y pulsa su **＋**. Ese elemento entra en el ' +
          'grafo del servicio: hasta que no está aquí, no se puede enlazar.',
        tip: 'Si el elemento ya estaba en el lienzo, el botón ＋ avisa en vez de duplicarlo.',
        autoNote: 'En la demo lo traigo yo con el botón ＋ del catálogo.',
        target: '.sd-canvas',
        placement: 'top',
        writes: true,
        auto: [
          { canvasAdd: 'RADIUS Corporativo' },
          { wait: 600 },
        ],
        advance: 'auto',
      },
      {
        id: 'enlazar',
        title: 'Enlázala con el RADIUS (se autentica en)',
        body:
          'El gesto de siempre, con **dos clics** y una ventana:\n' +
          '1. Clic en la tarjeta de la **VPN** (el origen).\n' +
          '2. Clic en la tarjeta del **RADIUS Corporativo** (el destino).\n' +
          '3. En la ventana, elige **se autentica en** y guarda.\n\n' +
          'Al guardar, el lienzo se redibuja y **aparece la flecha**: ése es el enlace.',
        tip: 'El orden de los clics marca la dirección: primero el origen, luego el destino. Con ⇄ se invierte.',
        autoNote: 'En la demo hago yo esos mismos dos clics y elijo el mismo tipo de relación, para que veas el gesto completo.',
        target: '.sd-canvas',
        placement: 'top',
        writes: true,
        auto: [
          { canvasClick: 'VPN corporativa' },
          { canvasClick: 'RADIUS Corporativo' },
          { relationModal: 'authenticates_on' },
        ],
        measure: (ctx) => ctx.edgesCount(),
        detect: (ctx, step) => ctx.edgesCount() > (step.baseline || 0),
        advance: 'auto',
      },
      {
        id: 'traer-firewall',
        title: 'Y ahora el firewall que la publica',
        body:
          'Trae del catálogo el **FW Virtual (pfSense)** con su **＋** y repite el gesto. Esta vez el sentido es el ' +
          'contrario: primero la **VPN** y luego el **firewall**, con el tipo **protegido por**.',
        tip: 'Una VPN sin el firewall que la publica es media historia: los dos deberían estar en el mapa.',
        autoNote: 'En la demo lo traigo y lo enlazo yo, igual que el RADIUS.',
        target: '.sd-canvas',
        placement: 'top',
        writes: true,
        auto: [
          { canvasAdd: 'FW Virtual' },
          { canvasClick: 'VPN corporativa' },
          { canvasClick: 'FW Virtual' },
          { relationModal: 'protected_by' },
        ],
        measure: (ctx) => ctx.edgesCount(),
        detect: (ctx, step) => ctx.edgesCount() > (step.baseline || 0),
        advance: 'auto',
      },
      {
        id: 'ver-ficha',
        title: 'Y ahora mira la ficha',
        body:
          'Vuelve al catálogo, abre tu VPN y entra en **Dependencias**. Las dos relaciones que acabas de dibujar ' +
          'salen ahí con su tipo (**se autentica en**, **protegido por**) y **con enlace a la ficha del otro**: ' +
          'puedes saltar del RADIUS a la VPN y vuelta.',
        tip: 'Esa lista la rellenan las relaciones del Diseñador; ni el nombre ni el tipo hay que escribirlos otra vez.',
        route: '/services',
        target: '.sf-rel-grid',
        placement: 'top',
        auto: [
          { openService: NOMBRE_VPN },
          { tab: 'Dependencias' },
          { wait: 1400 },
        ],
        detect: (ctx) => ctx.count('.sf-rel-row') > 0,
        advance: 'auto',
      },
      {
        id: 'mapa-global',
        title: 'Y éste es el mapa de todo el sistema',
        body:
          'El **Mapa de dependencias** dibuja todos los servicios de la empresa y todo lo que los une: no un ' +
          'servicio suelto, sino el conjunto. La VPN que acabas de montar aparece ya enganchada al RADIUS.\n\n' +
          'Pincha un nodo para ver sus entrantes y salientes resaltados.',
        tip: 'Es la vista para responder «si se cae esto, ¿qué más se cae?» sin abrir ficha por ficha.',
        route: '/services/dependency-map',
        target: null,
        placement: 'top',
        auto: [{ wait: 1200 }, { waitFor: 'svg', optional: true }, { wait: 1500 }],
        advance: 'manual',
      },
      {
        id: 'cierre',
        title: '¡Listo! Ese es un servicio bien modelado',
        body:
          'Un servicio útil en la CMDB tiene cuatro cosas: **tipo**, **dónde corre**, **por dónde se entra** y ' +
          '**con qué se relaciona** (y con quién, y en qué dirección). Ya tienes las cuatro, y el mapa lo enseña.\n\n' +
          'Desde aquí puedes repetirlo con otro servicio o deshacer lo creado en el tutorial.',
        tip: 'Cuando las fichas enseñan sus relaciones y hay un mapa que las une, el inventario deja de ser una lista de cajas.',
        target: null,
        advance: 'manual',
        final: true,
      },
    ],
  },
  {
    id: 'rds',
    icon: '🖥️',
    title: 'Montar una granja de escritorio remoto',
    subtitle: 'RDS → sesiones por RDP → AD y firewall',
    summary:
      'Creas el servicio de granja RDS, declaras el acceso RDP, dices en qué máquinas corre y lo enlazas con el AD que valida a los usuarios y el firewall que la protege.',
    minutes: 11,
    level: 'Intermedio',
    steps: [
      {
        id: 'intro',
        title: 'Una granja de escritorio remoto',
        body:
          'RDS es el servicio que da escritorios y aplicaciones por Escritorio Remoto. En el inventario interesa ' +
          'saber tres cosas:\n' +
          '· qué máquinas forman la granja,\n' +
          '· por dónde entran los usuarios (**RDP**),\n' +
          '· y contra quién validan (**AD**), protegidas por un **firewall**.',
        tip: 'En la CMDB este servicio tiene un tipo propio: «RDS / Granja Escritorio Remoto», con editor de topología.',
        route: '/services',
        target: null,
        advance: 'manual',
      },
      {
        id: 'nuevo',
        title: 'Crea el servicio',
        body: 'Pulsa **+ Nuevo servicio** para abrir una ficha en blanco.',
        target: '.wol-header .wol-btn-primary',
        placement: 'bottom',
        auto: [
          { click: '.wol-header .wol-btn-primary' },
          { waitFor: '.dv-overlay' },
        ],
        detect: (ctx) => ctx.has('.dv-overlay'),
        advance: 'auto',
      },
      {
        id: 'titulo',
        title: 'Nombre visible',
        body: `Escribe **${NOMBRE_RDS}**.`,
        target: 'input.dv-record-title',
        placement: 'bottom',
        auto: [{ fill: ['input.dv-record-title', NOMBRE_RDS] }],
        detect: (ctx) => ctx.value('input.dv-record-title').length > 3,
        advance: 'auto',
      },
      {
        id: 'tipo',
        title: 'Tipo: granja RDS',
        body:
          'En **Identificación → Tipo** elige **RDS / Granja Escritorio Remoto**. Fíjate que entonces aparece una ' +
          'pestaña nueva de **🌐 Topología** para dibujar la granja por dentro.',
        target: 'select[name="service_type"]',
        placement: 'left',
        auto: [{ select: ['select[name="service_type"]', 'rds_farm'] }],
        detect: (ctx) => ctx.value('select[name="service_type"]') === 'rds_farm',
        advance: 'auto',
      },
      {
        id: 'nombre-interno',
        title: 'Nombre interno',
        body: 'Escribe `rds-tutorial` en **Nombre interno** (obligatorio para guardar).',
        target: 'input[name="name"]',
        placement: 'bottom',
        auto: [{ fill: ['input[name="name"]', 'rds-tutorial'] }],
        detect: (ctx) => ctx.value('input[name="name"]').length > 2,
        advance: 'auto',
      },
      {
        id: 'criticidad',
        title: 'Criticidad: alta',
        body:
          'En **Criticidad** pon **Alto** o **Crítico**: si se cae la granja, la gente no puede trabajar. ' +
          'La criticidad se usa luego para priorizar en los avisos del panel.',
        target: 'select[name="criticality"]',
        placement: 'left',
        auto: [{ select: ['select[name="criticality"]', 'high'] }],
        detect: (ctx) => ['high', 'critical'].includes(ctx.value('select[name="criticality"]')),
        advance: 'auto',
      },
      {
        id: 'guardar',
        title: 'Guarda',
        body: 'Pulsa **Guardar**. El servicio ya existe y podemos engancharle accesos, máquinas y relaciones.',
        target: '.dv-topbar-actions .dv-btn-primary',
        placement: 'left',
        writes: true,
        trackServiceName: NOMBRE_RDS,
        auto: [
          { save: true, trackName: NOMBRE_RDS },
          { wait: 1200 },
        ],
        detect: (ctx) => ctx.hasServiceNamed(NOMBRE_RDS),
        advance: 'auto',
      },
      {
        id: 'acceso-rdp',
        title: 'El acceso por RDP',
        body:
          'Pestaña **🔗 Accesos** → **Añadir acceso**. Ponle nombre («Escritorio remoto»), tipo **Terminal Server / RDS** ' +
          'y el puerto **3389**.',
        tip: 'Para la granja, un acceso de tipo RDP o Terminal Server; si publicas una web desde el mismo servidor, añade también el HTTPS.',
        autoNote: 'En la demo lo doy de alta por la API; en la pantalla se hace con «Añadir acceso».',
        target: null,
        placement: 'bottom',
        writes: true,
        auto: [
          { tab: 'Accesos' },
          { addAccess: { name: 'Escritorio remoto', access_type: 'terminal_server', protocol: 'tcp', port: 3389, notes: 'Creado por el tutorial guiado' } },
        ],
        detect: (ctx) => ctx.smartCount('Accesos') > 0,
        advance: 'auto',
      },
      {
        id: 'hosts',
        title: 'Las máquinas de la granja',
        body:
          'Pestaña **Alojamiento** → en «Equipos (CIs)» añade cada servidor de la granja (o la VM, si RDS corre ' +
          'virtualizado). Dos servidores detrás de un balanceador es lo mínimo razonable.',
        autoNote: 'Este paso no lo hago solo: qué servidores forman tu granja depende de tu inventario.',
        target: null,
        placement: 'bottom',
        writes: true,
        detect: (ctx) => ctx.hostRows() > 0,
        advance: 'auto',
      },
      {
        id: 'topologia',
        title: 'Dibuja la granja por dentro',
        body:
          'Pestaña **🌐 Topología**: es el editor propio de las granjas RDS. Coloca el servidor de sesiones, los ' +
          'hosts de sesión y el acceso web tal como están montados.',
        tip: 'Esta topología se guarda con el servicio y sale luego en el informe; el fabricante y la versión ayudan a saber qué hay instalado.',
        autoNote: 'El editor de topología se rellena a mano (posiciones, nodos, puertos): te dejo la pestaña abierta para que la veas, pero no lo dibujo yo.',
        target: null,
        placement: 'bottom',
        auto: [{ tab: 'Topología' }],
        advance: 'manual',
      },
      {
        id: 'disenador',
        title: 'Al diseñador: el mapa de la granja',
        body:
          'Ahora las relaciones. Te llevo al **Diseñador** con la granja ya en el centro del lienzo; desde aquí se ' +
          'traen los demás elementos y se enlazan.',
        route: (state) => (state.serviceId ? `/services/designer?service=${state.serviceId}` : '/services/designer'),
        target: '.sd-canvas',
        placement: 'top',
        auto: [{ wait: 900 }, { waitFor: '.sd-canvas', optional: true }, { wait: 700 }],
        advance: 'manual',
      },
      {
        id: 'traer-ad',
        title: 'Trae el AD al mapa',
        body:
          'Busca el **AD Empresa A** en el catálogo de la izquierda y pulsa su **＋**: entra en el grafo de la granja ' +
          'y ya se puede enlazar.',
        autoNote: 'En la demo lo traigo yo con el botón ＋.',
        target: '.sd-canvas',
        placement: 'top',
        writes: true,
        auto: [
          { canvasAdd: 'AD Empresa A' },
          { wait: 600 },
        ],
        advance: 'auto',
      },
      {
        id: 'enlazar-ad',
        title: 'Enlázala con el AD (autenticado por)',
        body:
          'El gesto: **clic en la granja**, **clic en el AD** y en la ventana eliges **autenticado por**. Se lee ' +
          '«la granja está autenticada por el AD», que es lo que pasa de verdad: los usuarios validan contra el ' +
          'directorio.\n\n' +
          'Al guardar aparece la flecha en el lienzo.',
        tip: 'Si el elemento ya está en el lienzo, el ＋ avisa en vez de duplicarlo.',
        autoNote: 'En la demo hago los dos clics y elijo el tipo, igual que lo harías tú.',
        target: '.sd-canvas',
        placement: 'top',
        writes: true,
        auto: [
          { canvasClick: 'Granja RDS' },
          { canvasClick: 'AD Empresa A' },
          { relationModal: 'authenticated_by' },
        ],
        measure: (ctx) => ctx.edgesCount(),
        detect: (ctx, step) => ctx.edgesCount() > (step.baseline || 0),
        advance: 'auto',
      },
      {
        id: 'enlazar-fw',
        title: 'Y con el firewall que la protege',
        body:
          'Repite con el **FW Virtual (pfSense)**: traer con **＋**, clic en la **granja**, clic en el **firewall** y ' +
          'tipo **protegido por**.',
        tip: 'Con el firewall en el mapa sabes por dónde entra el tráfico de los usuarios, no solo que existe.',
        autoNote: 'En la demo lo traigo y lo enlazo yo.',
        target: '.sd-canvas',
        placement: 'top',
        writes: true,
        auto: [
          { canvasAdd: 'FW Virtual' },
          { canvasClick: 'Granja RDS' },
          { canvasClick: 'FW Virtual' },
          { relationModal: 'protected_by' },
        ],
        measure: (ctx) => ctx.edgesCount(),
        detect: (ctx, step) => ctx.edgesCount() > (step.baseline || 0),
        advance: 'auto',
      },
      {
        id: 'ver-ficha',
        title: 'Comprueba la ficha',
        body:
          'Vuelve al catálogo y abre la granja: en **Dependencias** verás el AD y el firewall enlazados, con el tipo ' +
          'de relación y un enlace para saltar a su ficha.',
        route: '/services',
        target: '.sf-rel-grid',
        placement: 'top',
        auto: [
          { openService: NOMBRE_RDS },
          { tab: 'Dependencias' },
          { wait: 1400 },
        ],
        detect: (ctx) => ctx.count('.sf-rel-row') > 0,
        advance: 'auto',
      },
      {
        id: 'mapa-global',
        title: 'El mapa completo de servicios',
        body:
          'Y para verlo todo junto: el **Mapa de dependencias**. Aquí están todos los servicios de la empresa y sus ' +
          'enlaces; la granja que acabas de montar sale con el AD y el firewall colgando.',
        tip: 'Este mapa es la vista de conjunto: sirve para explicar la infraestructura a alguien que no la conoce.',
        route: '/services/dependency-map',
        target: null,
        placement: 'top',
        auto: [{ wait: 1200 }, { waitFor: 'svg', optional: true }, { wait: 1500 }],
        advance: 'manual',
      },
      {
        id: 'cierre',
        title: 'Granja modelada',
        body:
          'Ya sabes montar un servicio de principio a fin: tipo, dónde corre, por dónde se entra, cómo se relaciona ' +
          'y cómo se revisa después.',
        target: null,
        advance: 'manual',
        final: true,
      },
    ],
  },
  {
    id: 'mapa',
    icon: '🗺️',
    title: 'Ordenar el mapa de un servicio que ya existe',
    subtitle: `Partimos del ${SERVICIO_RADIUS} real y le añadimos lo que le falta`,
    summary:
      'No se crea nada: se abre un servicio que ya está en el inventario, se lee lo que tiene montado, se traen sus vecinos al lienzo y se añade la relación que faltaba. Es el trabajo del día a día: mantener el mapa al día.',
    minutes: 8,
    level: 'Avanzado',
    serviceRef: SERVICIO_RADIUS,
    steps: [
      {
        id: 'intro',
        title: 'Aquí no creamos nada: ordenamos',
        body:
          `Los tutoriales anteriores montaban un servicio desde cero. Éste es el otro trabajo, el que se hace cada ` +
          `semana: un servicio que **ya existe** (el ${SERVICIO_RADIUS}) y hay que **revisar su mapa**.\n` +
          '· primero se lee lo que ya tiene,\n' +
          '· después se traen al lienzo los elementos que le afectan,\n' +
          '· y se añade lo que falte.\n\n' +
          'Todo lo que se cree aquí queda apuntado y se puede **deshacer** al terminar.',
        tip: 'Trabajar sobre datos reales es lo normal en una CMDB: por eso el tutorial apunta cada cambio para poder revertirlo.',
        route: '/services',
        target: null,
        advance: 'manual',
      },
      {
        id: 'abrir-ficha',
        title: 'Abre la ficha y mira sus relaciones',
        body:
          `Abro el **${SERVICIO_RADIUS}** y me voy a la pestaña **Dependencias**. Ahí están sus relaciones: a la ` +
          'izquierda lo que **necesita** y a la derecha quién **depende de él**.',
        tip: 'El nombre entre corchetes y el tipo son los del catálogo de relaciones; el enlace de cada fila abre la ficha del otro servicio.',
        target: '.sf-rel-grid',
        placement: 'top',
        auto: [
          { openService: SERVICIO_RADIUS },
          { tab: 'Dependencias' },
          { wait: 1400 },
        ],
        detect: (ctx) => ctx.count('.sf-rel-row') > 0,
        advance: 'auto',
      },
      {
        id: 'leer-relaciones',
        title: 'Cada flecha tiene dirección y tipo',
        body:
          'Fíjate en que no es una lista de nombres: cada fila dice **quién** se relaciona y **de qué manera** ' +
          '(se autentica en, autenticado por, protegido por…). Eso es lo que hace útil el inventario: saber en qué ' +
          'sentido va la dependencia.',
        tip: 'Si una fila no dice nada («depende de» a secas), es una relación mal tipada: merece la pena arreglarla.',
        target: '.sf-rel-grid',
        placement: 'top',
        auto: [{ wait: 600 }],
        advance: 'manual',
      },
      {
        id: 'mapa-servicio',
        title: 'La vista general: el mapa de ESTE servicio',
        body:
          'Pestaña **Vista general**. Aquí no ves el inventario entero, sino el servicio en el centro con todo lo que ' +
          'le cuelga: dónde corre, qué necesita y quién lo usa. Es la vista para explicar un servicio concreto.',
        tip: 'Se puede arrastrar cada caja para recolocar el dibujo; los cambios son solo visuales.',
        target: '.som-wrap',
        placement: 'top',
        auto: [
          { tab: 'Vista general' },
          { wait: 1600 },
        ],
        detect: (ctx) => ctx.has('.som-wrap'),
        advance: 'auto',
      },
      {
        id: 'disenador',
        title: 'Al Diseñador a retocar el mapa',
        body:
          'Ahora al **Diseñador**, que es donde se edita de verdad: nodos, uniones y tipos de relación. Te llevo con ' +
          `el ${SERVICIO_RADIUS} ya cargado.`,
        tip: 'El Diseñador también se abre desde el menú: Servicios → Diseñador de servicios.',
        route: (state) => (state.serviceId ? `/services/designer?service=${state.serviceId}` : '/services/designer'),
        target: '.sd-canvas',
        placement: 'top',
        auto: [{ wait: 900 }, { waitFor: '.sd-canvas', optional: true }, { wait: 900 }],
        advance: 'manual',
      },
      {
        id: 'traer-vecinos',
        title: 'Trae al lienzo los vecinos que le afectan',
        body:
          'Un mapa se ordena **trayendo al lienzo** lo que rodea al servicio. Voy a añadir el **AD Empresa A** (contra ' +
          'el que valida las credenciales) y el **FW Virtual (pfSense)** que está delante.\n\n' +
          'Fíjate en lo importante: al entrar, **la flecha que ya existía aparece dibujada sola**. El mapa no inventa ' +
          'relaciones, las enseña.',
        tip: 'El botón ＋ está en la fila de cada resultado del catálogo de la izquierda; si el elemento ya estaba, no se duplica.',
        autoNote: 'En la demo traigo yo los dos con el botón ＋ del catálogo.',
        target: '.sd-canvas',
        placement: 'top',
        auto: [
          { canvasAdd: 'AD Empresa A', optional: true },
          { wait: 700 },
          { canvasAdd: 'FW Virtual', optional: true },
          { wait: 900 },
        ],
        advance: 'manual',
      },
      {
        id: 'anadir-falta',
        title: 'Y añade la relación que faltaba',
        body:
          `Ahora lo que no estaba: el ${SERVICIO_RADIUS} **no estaba marcado como protegido por el firewall**. Se ` +
          'añade con el gesto de siempre:\n' +
          '1. Clic en el nodo del **' + SERVICIO_RADIUS + '** (el origen).\n' +
          '2. Clic en el nodo del **FW Virtual** (el destino).\n' +
          '3. En la ventana, **protegido por** y guardar.',
        tip: 'Antes de crear una relación, mira si ya existe: duplicarla no aporta y ensucia el mapa. Aquí no estaba.',
        autoNote: 'En la demo hago los dos clics y elijo «protegido por». Queda apuntado y se puede deshacer.',
        target: '.sd-canvas',
        placement: 'top',
        writes: true,
        auto: [
          { canvasClick: SERVICIO_RADIUS },
          { canvasClick: 'FW Virtual' },
          { relationModal: 'protected_by' },
        ],
        measure: (ctx) => ctx.edgesCount(),
        detect: (ctx, step) => ctx.edgesCount() > (step.baseline || 0),
        advance: 'auto',
      },
      {
        id: 'comprobar',
        title: 'Comprueba que la ficha ya lo cuenta',
        body:
          'Vuelvo a la ficha: en **Dependencias** tiene que salir la relación nueva, con su tipo y su enlace. Si no ' +
          'aparece, es que se guardó en otro servicio: revisa la dirección de los clics.',
        tip: 'El mapa global y la ficha leen las mismas relaciones: si una no cuadra con la otra, es un fallo, no una opinión.',
        route: '/services',
        target: '.sf-rel-grid',
        placement: 'top',
        auto: [
          { openService: SERVICIO_RADIUS },
          { tab: 'Dependencias' },
          { wait: 1500 },
        ],
        measure: (ctx) => ctx.count('.sf-rel-row'),
        detect: (ctx, step) => ctx.count('.sf-rel-row') > (step.baseline || 0),
        advance: 'auto',
      },
      {
        id: 'mapa-global',
        title: 'El conjunto: quién depende de quién',
        body:
          'Y el **Mapa de dependencias** para verlo todo junto. Aquí se ve si el RADIUS está en el centro de muchas ' +
          'cosas (lo está: es el ejemplo perfecto de servicio del que todos dependen) y qué se cae si se cae él.\n\n' +
          'Pulsa un nodo para resaltar sus entrantes y salientes.',
        tip: 'Para responder «¿qué se cae si esto se cae?» esta vista ahorra abrir ficha por ficha.',
        route: '/services/dependency-map',
        target: null,
        placement: 'top',
        auto: [{ wait: 1200 }, { waitFor: 'svg', optional: true }, { wait: 1500 }],
        advance: 'manual',
      },
      {
        id: 'cierre',
        title: 'Eso es mantener una CMDB viva',
        body:
          'Montar servicios es la mitad del trabajo; la otra mitad es esto: **leer lo que hay, traer al mapa lo que ' +
          'afecta y añadir lo que falta**, sin duplicar ni inventar.\n\n' +
          'Si no quieres quedarte la relación que has creado, pulsa **Deshacer lo del tutorial** en la página de ' +
          'tutoriales: se borra solo lo que ha creado este guion.',
        tip: 'Repite este guion con cualquier servicio del inventario: cambia el sujeto, no el método.',
        target: null,
        advance: 'manual',
        final: true,
      },
    ],
  },
];

export function getTutorial(id) {
  return TUTORIALS.find(t => t.id === id) || null;
}
